import { useEffect, useRef, type CSSProperties, type JSX } from "react";
import { BowSigil } from "../components/Wordmark";
import { useReducedMotion } from "../hooks/use-reduced-motion";
import type { Language } from "../strings";
import type { Strings } from "../strings/types";
import { CorpusMarkdown } from "./CorpusMarkdown";
import type { CorpusIndexShort } from "./corpus-types";
import {
  flechaAskPrompt,
  flechaDisplayTitle,
  showFlechaChatAvailabilityNotice,
} from "./flechas";
import { useCorpusShort } from "./use-corpus-data";

interface FlechasPaneProps {
  videoId: string;
  entries: CorpusIndexShort[];
  anchor: string | null;
  scale: number;
  language: Language;
  strings: Strings;
  onAsk: (prompt: string) => void;
  onOpen: (videoId: string) => void;
  onIndex: () => void;
}

const NO_LAYERS: ReadonlySet<string> = new Set();

function sectionLabel(headings: string[]): string | null {
  const heading = headings[0];
  if (!heading) return null;
  const bracketed = /^\[([^\]]+)\]/.exec(heading);
  return (bracketed?.[1] ?? heading).trim() || null;
}

function scrollParentOf(element: HTMLElement | null): HTMLElement | null {
  for (let parent = element?.parentElement ?? null; parent; parent = parent.parentElement) {
    const overflow = window.getComputedStyle(parent).overflowY;
    if (overflow === "auto" || overflow === "scroll") return parent;
  }
  return null;
}

export function FlechasPane(props: FlechasPaneProps): JSX.Element {
  const { videoId, entries, anchor, scale, language, strings, onAsk, onOpen, onIndex } = props;
  const content = useCorpusShort(videoId);
  const reducedMotion = useReducedMotion();
  const paneRef = useRef<HTMLDivElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (content.state !== "ready") return;
    let secondFrame = 0;
    const firstFrame = requestAnimationFrame(() => {
      secondFrame = requestAnimationFrame(() => {
        if (anchor) {
          paneRef.current
            ?.querySelector<HTMLElement>(`[data-anchor="${CSS.escape(anchor)}"]`)
            ?.scrollIntoView({ behavior: reducedMotion ? "auto" : "smooth", block: "start" });
        } else {
          scrollParentOf(paneRef.current)?.scrollTo({ top: 0 });
        }
      });
    });
    return () => {
      cancelAnimationFrame(firstFrame);
      cancelAnimationFrame(secondFrame);
    };
  }, [videoId, anchor, content.state, reducedMotion]);

  useEffect(() => {
    if (content.state !== "ready") return;
    const scroller = scrollParentOf(paneRef.current);
    if (!scroller) return;
    let frame = 0;
    const update = (): void => {
      frame = 0;
      const maximum = scroller.scrollHeight - scroller.clientHeight;
      const progress = maximum > 0 ? Math.min(1, Math.max(0, scroller.scrollTop / maximum)) : 0;
      if (progressRef.current) progressRef.current.style.transform = `scaleX(${progress})`;
    };
    const onScroll = (): void => {
      if (frame === 0) frame = requestAnimationFrame(update);
    };
    update();
    scroller.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      if (frame) cancelAnimationFrame(frame);
      scroller.removeEventListener("scroll", onScroll);
    };
  }, [content.state]);

  const es = language === "es";
  return (
    <div className="freader-layout">
      <aside className="freader-aside">
        <button type="button" className="freader-aside__back" onClick={onIndex}>
          <span aria-hidden="true">←</span> {es ? "Todas las Flechas" : "All Flechas"}
        </button>
        <nav className="freader-aside__nav" aria-label={es ? "Flechas" : "Flechas"}>
          {entries.map((entry) => (
            <button
              key={entry.entry_id}
              type="button"
              className={`freader-aside__entry${entry.video_id === videoId ? " is-active" : ""}`}
              aria-current={entry.video_id === videoId ? "page" : undefined}
              onClick={() => onOpen(entry.video_id)}
            >
              {flechaDisplayTitle(
                entry.title,
                es ? "Aclaración de Koresh" : "Koresh short teaching",
              )}
            </button>
          ))}
        </nav>
      </aside>
      <main className="creader-main">
        {content.state === "loading" ? <p className="creader-note">{strings.corpusLoading}</p> : null}
        {content.state === "empty" ? <p className="creader-note">{strings.corpusShortNotFound}</p> : null}
        {content.state === "error" ? <p className="creader-note creader-note--err">{strings.corpusShortError}</p> : null}
        {content.state === "ready" ? (
          <div
            ref={paneRef}
            className="creader-pane freader-pane"
            style={{ "--reader-scale": String(scale) } as CSSProperties}
          >
            <div className="creader-progress" aria-hidden="true">
              <div ref={progressRef} className="creader-progress__bar" />
            </div>
            <header className="creader-pane__head freader-pane__head">
              <span className="creader-pane__kicker">Flechas · {es ? "Aclaración corta" : "Short explanation"}</span>
              <h1 className="creader-pane__title">
                {flechaDisplayTitle(
                  content.data.title,
                  es ? "Aclaración de Koresh" : "Koresh short teaching",
                )}
              </h1>
              <div className="creader-pane__arrow" aria-hidden="true">
                <span className="creader-hero__arrowline" />
                <span className="creader-hero__arrowhead" />
              </div>
              <p className="freader-pane__lede">
                {es
                  ? "Una respuesta breve de Koresh, conservada como enseñanza temática y no como capítulo bíblico."
                  : "A brief answer from Koresh, preserved as topical teaching rather than a Bible chapter."}
              </p>
              {content.data.editorial_note ? (
                <p className="creader-pane__chatnote">{content.data.editorial_note}</p>
              ) : null}
              {showFlechaChatAvailabilityNotice(content.data.status) ? (
                <p className="creader-pane__chatnote">
                  <BowSigil className="creader-pane__chatnote-sigil" />
                  {strings.corpusShortNotIndexedNotice}
                </p>
              ) : null}
            </header>
            {content.data.chunks.map((chunk) => (
              <article
                key={chunk.anchor}
                id={chunk.anchor}
                data-anchor={chunk.anchor}
                className="creader-chunk"
              >
                <div className="creader-chunk__source">
                  <BowSigil className="creader-chunk__sigil" />
                  <span className="creader-chunk__src-text">
                    {flechaDisplayTitle(
                      chunk.source.title ?? content.data.title,
                      es ? "Aclaración de Koresh" : "Koresh short teaching",
                    )}
                    {chunk.source.timestamp_label ? (
                      <span className="creader-chunk__ts"> · {chunk.source.timestamp_label}</span>
                    ) : null}
                  </span>
                  {chunk.source.url ? (
                    <a
                      className="creader-chunk__yt"
                      href={chunk.source.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {strings.corpusWatchOnYouTube} ↗
                    </a>
                  ) : null}
                </div>
                <div className="creader-md">
                  <CorpusMarkdown text={chunk.md} anchor={chunk.anchor} prism={null} activeLayers={NO_LAYERS} />
                </div>
                <div className="creader-chunk__foot">
                  <button
                    type="button"
                    className="creader-chunk__ask"
                    onClick={() =>
                      onAsk(
                        flechaAskPrompt(
                          language,
                          content.data.title,
                          chunk.headings.length === 1 ? sectionLabel(chunk.headings) : null,
                        ),
                      )
                    }
                  >
                    {strings.corpusAskAbout}
                  </button>
                </div>
              </article>
            ))}
          </div>
        ) : null}
      </main>
    </div>
  );
}
